import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DataTable } from '@airclou/ui';
import { Plus, UsersRound, Trash2 } from 'lucide-react';
import type { WorkspaceGroup } from '@/api/workspaces';
import { RemoveWorkspaceGroupModal } from './RemoveWorkspaceGroupModal';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'
import { RoleUnknownNotice } from '../state/RoleUnknownNotice'

type Props = {
  workspaceId: string;
  groups: WorkspaceGroup[];
  sorting?: any;
  onSortingChange?: (value: any) => void;
  onAddGroup: () => void;
  onGroupRemoved: () => void;
  /** The page's first load. The table keeps its frame and shows a loading row,
   *  and the empty state is held back until the rows have answered — "no groups
   *  yet" must not flash on a workspace that has some. */
  isLoading?: boolean;
};

export function WorkspaceGroupsTab({ workspaceId, groups, sorting = [], onSortingChange = () => {}, onAddGroup, onGroupRemoved, isLoading = false }: Props) {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  // Assigning and unassigning groups is `MANAGE` on the API as of 2026-09-13
  // (role plan §6.4); `canManageOrg` mirrors that set.
  const { user } = useAuth();
  const canManage = canManageOrg(user?.orgRole ?? undefined);
  const [groupToRemove, setGroupToRemove] = useState<WorkspaceGroup | null>(null);

  const columns = [
    {
      id: 'group_name',
      header: t('common.groups'),
      cell: (group: WorkspaceGroup) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30">
            <UsersRound className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <div className="font-medium text-foreground">
              {group.group_name}
            </div>
            {group.group_description && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {group.group_description}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'member_count',
      header: t('common.members'),
      cell: (group: WorkspaceGroup) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {group.member_count} {group.member_count === 1 ? t('common.member').toLowerCase() : t('common.members').toLowerCase()}
        </div>
      ),
    },
    {
      id: 'created_at',
      header: t('common.added'),
      cell: (group: WorkspaceGroup) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateInTz(group.created_at, timezone)}
        </div>
      ),
    },
    // The column goes rather than emptying — it reserves its width either way.
    ...(canManage ? [{
      id: 'actions',
      header: '',
      cell: (group: WorkspaceGroup) => (
        <div className="flex items-center justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setGroupToRemove(group)}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            {t('common.remove')}
          </Button>
        </div>
      ),
    }] : []),
  ];

  const addButton = canManage ? (
    <Button onClick={onAddGroup}>
      <Plus className="w-4 h-4 mr-2" />
      {t('workspaceTabs.addGroup')}
    </Button>
  ) : null;

  return (
    <div>
      <div className="pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t('workspaceTabs.groupsTitle')}</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('workspaceTabs.groupsDesc')}
            </p>
          </div>
          {addButton}
        </div>
        <RoleUnknownNotice />
      </div>

      <div>
        {groups.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <UsersRound className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">{t('workspaceTabs.noGroupsYet')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {t('workspaceTabs.noGroupsHelp')}
            </p>
            {addButton}
          </div>
        ) : (
          <DataTable data={groups} columns={columns} sorting={sorting} onSortingChange={onSortingChange} isLoading={isLoading && groups.length === 0} />
        )}
      </div>

      {/* Info callout */}
      {groups.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-sm text-primary">
            <strong>{t('common.note')}:</strong> {t('workspaceTabs.groupNote')}
          </p>
        </div>
      )}

      {/* Remove confirmation modal */}
      {groupToRemove && (
        <RemoveWorkspaceGroupModal
          isOpen={!!groupToRemove}
          onClose={() => setGroupToRemove(null)}
          onSuccess={() => {
            setGroupToRemove(null);
            onGroupRemoved();
          }}
          workspaceId={workspaceId}
          groupId={groupToRemove.group_id}
          groupName={groupToRemove.group_name}
        />
      )}
    </div>
  );
}
